/// <amd-module name="WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase" />
import { WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import { DemandTracker } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/DemandTracker";
import { DemandType } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/DemandType";
import { IDataManager } from "Reporting/Visuals/LayoutComponents/Widgets/IDataManager";
import { isErrorMessageType, LayoutState, MessageType, packMessageAsState } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { IVssPageContext } from "VSS/Platform/Context";

/** Describes options neccessary for constructing a data manager used with a versioned widget. (Aspects such as size are intentionally hidden away as irrelevant - such asks need to come in as demands).*/
export interface WidgetDataManagerOptions {
    title: string;

    /** Allows for consumer to request title be omitted from presentation, as redundant for page level experiences. */
    hideTitle?: boolean;

    settings: string | null;

    /** Signifies if animations are suppressed. This is relevant for widget configuration reload scenarios, where animations during param changes are obnoxious. */
    suppressAnimations: boolean;

    /** This member is only to support Widget telemetry.
     *  For legacy reasons, this member is redundant with other props on the data manager.
     */
    widgetTelemetryProps: WidgetProps;
}

/** Responsible for intelligently handling requests of data on the widget, and generating state objects for use by the store.
 *  The base class contains commonly managed state, and covers routine "registerDemand" bookkeeping, so derived types can focus on the heavy lifting of "getData()" implementation.
 */
export abstract class WidgetDataManagerBase implements IDataManager {
    protected context: IVssPageContext;
    protected title: string;
    protected hideTitle?: boolean;
    protected settings: string | null;
    protected widgetPropsForTelemetry: WidgetProps;
    protected suppressAnimations: boolean;

    // In the beginning, state is empty. As demands are registered, state is expanded.
    protected currentState: LayoutState = {} as LayoutState;

    protected demandTracker: DemandTracker;

    constructor(context: IVssPageContext, options: WidgetDataManagerOptions) {
        this.update(options);
        this.context = context;
        this.demandTracker = new DemandTracker();
    }

    /** Default implementation- Sets up initial state to reflect demanded information. */
    public registerDemand(demand: DemandType): void {
        if (!this.demandTracker.isDemandPresent(demand)) {
            this.demandTracker.registerDemand(demand);

            // Title information is available any time after creation. For everything else, there's getData().
            switch (demand) {
                case DemandType.title:
                    this.currentState.title = { text: this.title };
                    break;
                case DemandType.subtitle:
                case DemandType.scalar:
                case DemandType.chart:
                case DemandType.submetrics:
                    break;
                default:
                    throw new Error("An unrecognized demand was issued to the DataManager.");
            }
        }
    }

    public update(options: WidgetDataManagerOptions): void {
        this.title = options.title;
        this.settings = options.settings;
        this.suppressAnimations = options.suppressAnimations;
        this.widgetPropsForTelemetry = options.widgetTelemetryProps;
        this.hideTitle = options.hideTitle;
    }

    public getInitialState(): LayoutState {
        return this.currentState;
    }

    // Updates state based on message type
    public async packMessageAsState(messageType: MessageType, message?: string): Promise<LayoutState> {
        this.currentState = packMessageAsState(messageType, message);

        return isErrorMessageType(messageType) ? Promise.reject(this.currentState) : Promise.resolve(this.currentState);
    }

    /** All implementations must repond to demands issued on them in the getData method. */
    public abstract getData(): Promise<LayoutState>;
}
